import { useState } from 'react';
import { api, formatDateTime, formatNumber, formatRelative } from '../api';
import { usePolling } from '../auth';
import { Badge, Empty, Figure, Note, Panel, Spinner } from '../components/ui';
import type { NewsCheckOutcome, NewsIntent, NewsOverview, NewsSymbolResponse, NewsVerdict } from '../types';

type Toast = (message: string, tone?: 'success' | 'error') => void;

const INTENT_LABEL: Record<NewsIntent, string> = {
  open_long: 'new long',
  open_short: 'new short',
  exit_long: 'exit long',
  exit_short: 'exit short',
  stop_long: 'stop (long)',
  stop_short: 'stop (short)',
};

const VERDICT_LABEL: Record<NewsVerdict, string> = {
  NO_NEWS: 'no news',
  PROCEED: 'go ahead',
  DELAY: 'delayed',
  BLOCK: 'blocked',
  HOLD: 'kept open',
  OVERRIDE: 'held through stop',
  FORCED: 'stop enforced',
  UNAVAILABLE: 'unchecked',
};

const VERDICT_TONE: Record<NewsVerdict, 'green' | 'red' | 'amber' | 'blue' | 'grey'> = {
  NO_NEWS: 'grey',
  PROCEED: 'green',
  DELAY: 'amber',
  BLOCK: 'red',
  HOLD: 'blue',
  OVERRIDE: 'blue',
  FORCED: 'red',
  UNAVAILABLE: 'grey',
};

const signed = (value: number | null | undefined, digits = 1): string =>
  value === null || value === undefined || !Number.isFinite(value) ? '—' : `${value >= 0 ? '+' : ''}${value.toFixed(digits)}%`;

/**
 * A headline, linked safely. Google News headlines link to a Google News search
 * for the title (the raw link is an opaque redirect to an unverified host), and
 * are marked "via Google" so a click is never a surprise. Filings and
 * announcements keep their direct link, since those go to the issuer or the SEC.
 */
function Headline({
  title,
  url,
  kind = 'headline',
}: {
  title: string;
  url: string | null;
  kind?: 'headline' | 'filing' | 'announcement';
}) {
  if (!url) return <>{title}</>;
  const viaGoogle = kind === 'headline';
  return (
    <>
      <a href={url} target="_blank" rel="noopener noreferrer nofollow">
        {title}
      </a>
      {viaGoogle && <span className="mono-small"> (via Google)</span>}
    </>
  );
}

/**
 * The news check: every stream's orders are read against the latest headlines,
 * SEC filings and London announcements before they execute. This page shows what
 * it is watching, what it decided, and how its interventions turned out.
 */
export default function News({ pushToast }: { pushToast: Toast }) {
  const overview = usePolling<NewsOverview>(() => api.newsOverview(), 60_000);
  const [scanning, setScanning] = useState(false);

  async function scan(): Promise<void> {
    setScanning(true);
    try {
      const { result } = await api.runNewsDiscovery();
      pushToast(
        result.ran
          ? `Scanned ${result.headlinesScanned} headlines: ${result.found.length} compan${result.found.length === 1 ? 'y' : 'ies'} in the news.`
          : `Not scanned: ${result.reason}.`,
        result.ran ? 'success' : 'error',
      );
      overview.reload();
    } catch (err) {
      pushToast(err instanceof Error ? err.message : 'Scan failed', 'error');
    } finally {
      setScanning(false);
    }
  }

  const data = overview.data;

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>News</h1>
          <p>
            Before any stream trades, a model reads the instrument's latest headlines, SEC filings and
            London announcements, and can let the trade go ahead, delay it or block it. It can also
            keep a position open that a rule would sell. Large companies in the news join every
            stream's candidates.
          </p>
        </div>
        <button className="btn" type="button" onClick={() => void scan()} disabled={scanning}>
          {scanning ? <Spinner label="Scanning" /> : 'Scan headlines now'}
        </button>
      </div>

      {overview.error && <Note tone="error">{overview.error}</Note>}
      {!data && !overview.error && <Spinner label="Loading the news check" />}

      {data && (
        <>
          {!data.settings.enabled && (
            <Note tone="warn" title="The news check is off">
              Set NEWS_ENABLED=true to turn it on. Orders currently execute without it.
            </Note>
          )}
          {data.modelBlockedUntil && (
            <Note tone="warn" title="News model allowance spent">
              Groq reports the daily allowance for {data.settings.model} is used up, so orders proceed
              unchecked until {formatDateTime(data.modelBlockedUntil)}.
            </Note>
          )}

          <Note tone="info" title="How it decides, and its limits">
            New positions can be delayed (re-examined next cycle) or blocked; exits can be kept open.
            A stop-loss can be held open on news for at most {data.settings.stopOverrideMaxDays} days,
            and never past a {data.settings.stopOverrideMaxLossPct}% loss. If the news or the model is
            unreachable, trades go ahead on the stream's own evidence and stops always execute. It
            cannot be backtested honestly, so it is scored live below: each intervention against what
            the price did over the next five trading days.
          </Note>

          <div className="grid grid-4">
            <Figure
              label="Checked today"
              value={`${data.today.checks}`}
              foot={`${data.today.withNews} had recent news${data.today.unavailable ? ` · ${data.today.unavailable} unchecked` : ''}`}
            />
            <Figure label="Interventions today" value={`${data.today.interventions}`} foot="delayed, blocked or held" />
            <Figure
              label="Interventions that helped"
              value={data.scorecard.scored === 0 ? '—' : `${data.scorecard.helped} / ${data.scorecard.scored}`}
              tone={
                data.scorecard.averageEffect5dPct === null
                  ? 'flat'
                  : data.scorecard.averageEffect5dPct > 0
                    ? 'up'
                    : 'down'
              }
              foot={
                data.scorecard.scored === 0
                  ? `${data.scorecard.pending} waiting for five trading days`
                  : `average ${signed(data.scorecard.averageEffect5dPct, 2)} over 5 days · ${data.scorecard.pending} pending`
              }
            />
            <Figure
              label="In the news"
              value={`${data.watchlist.filter((entry) => !entry.inUniverse).length}`}
              foot={`extra candidates · last scan ${formatRelative(data.lastDiscoveryAt)}`}
            />
          </div>

          <Panel
            title="In the news"
            note={`S&P 500 and FTSE 100 members with material news (list as of ${data.settings.constituentsAsOf || 'unknown'})`}
          >
            {data.watchlist.length === 0 ? (
              <Empty>
                No companies flagged yet. Headlines are scanned every {data.settings.discoveryIntervalMinutes} minutes
                while a market is open.
              </Empty>
            ) : (
              <div className="grid-wrap">
                <table aria-label="Companies in the news">
                  <thead>
                    <tr>
                      <th scope="col">Symbol</th>
                      <th scope="col">Company</th>
                      <th scope="col">News</th>
                      <th scope="col">Seen</th>
                      <th scope="col">Candidate until</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.watchlist.map((entry) => (
                      <tr key={entry.symbol}>
                        <td className="sym" style={{ whiteSpace: 'nowrap' }}>
                          {entry.symbol}
                          {entry.inUniverse && <div className="mini">in universe</div>}
                        </td>
                        <td>{entry.name}</td>
                        <td>
                          <div>{entry.why ?? '—'}</div>
                          {entry.headlines.slice(0, 2).map((headline) => (
                            <div key={headline.title} className="mini">
                              <Headline title={headline.title} url={headline.url} /> · {headline.source}
                            </div>
                          ))}
                        </td>
                        <td className="mono-small" style={{ whiteSpace: 'nowrap' }}>
                          {formatRelative(entry.lastSeen)}
                        </td>
                        <td className="mono-small" style={{ whiteSpace: 'nowrap' }}>
                          {formatDateTime(entry.expiresAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>

          <Panel title="Interventions" note="each counted once, scored on the next five trading days">
            {data.interventions.length === 0 ? (
              <Empty>The news check has not delayed, blocked or held anything yet.</Empty>
            ) : (
              <ChecksTable rows={data.interventions} scored />
            )}
          </Panel>

          <Panel title="Recent checks" note="every order, including those with no news">
            {data.recent.length === 0 ? <Empty>No orders checked yet.</Empty> : <ChecksTable rows={data.recent} />}
          </Panel>

          <SymbolLookup />
        </>
      )}
    </div>
  );
}

function ChecksTable({ rows, scored = false }: { rows: NewsCheckOutcome[]; scored?: boolean }) {
  return (
    <div className="grid-wrap">
      <table aria-label={scored ? 'News check interventions' : 'Recent news checks'}>
        <thead>
          <tr>
            <th scope="col">When</th>
            <th scope="col">Stream</th>
            <th scope="col">Symbol</th>
            <th scope="col">Order</th>
            <th scope="col">Verdict</th>
            <th scope="col">Why</th>
            {scored && (
              <>
                <th scope="col" className="num">
                  Price then
                </th>
                <th scope="col" className="num">
                  1 day
                </th>
                <th scope="col" className="num">
                  5 days
                </th>
                <th scope="col">Outcome</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <td className="mono-small" style={{ whiteSpace: 'nowrap' }}>
                {formatDateTime(row.createdAt)}
              </td>
              <td>{row.streamName}</td>
              <td className="sym">
                {row.newsSymbol}
                {row.symbol !== row.newsSymbol && <div className="mini">{row.symbol}</div>}
              </td>
              <td className="mono-small">{INTENT_LABEL[row.intent] ?? row.intent}</td>
              <td>
                <Badge tone={VERDICT_TONE[row.verdict] ?? 'grey'}>{VERDICT_LABEL[row.verdict] ?? row.verdict}</Badge>
              </td>
              <td className="reason" style={{ maxWidth: '52ch' }}>
                {row.reason ?? '—'}
              </td>
              {scored && (
                <>
                  <td className="num">
                    {row.price === null ? '—' : `${formatNumber(row.price, 2)} ${row.currency ?? ''}`}
                  </td>
                  <td className="num">{signed(row.move1dPct)}</td>
                  <td className="num">{signed(row.move5dPct)}</td>
                  <td>
                    {row.status === 'helped' ? (
                      <Badge tone="green">helped {signed(row.effect5dPct)}</Badge>
                    ) : row.status === 'hurt' ? (
                      <Badge tone="red">cost {signed(row.effect5dPct)}</Badge>
                    ) : (
                      <span className="mono-small">{row.status}</span>
                    )}
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SymbolLookup() {
  const [input, setInput] = useState('');
  const [symbol, setSymbol] = useState<string | null>(null);
  const [data, setData] = useState<NewsSymbolResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function load(next: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      setData(await api.newsSymbol(next));
      setSymbol(next);
    } catch (err) {
      setData(null);
      setError(err instanceof Error ? err.message : 'Could not load news');
    } finally {
      setBusy(false);
    }
  }

  const assessment = data?.assessment ?? null;

  return (
    <Panel title="Look up a symbol" note="the news the check would read now, and its last verdict">
      <form
        className="inline-form"
        onSubmit={(event) => {
          event.preventDefault();
          const next = input.trim().toUpperCase();
          if (next) void load(next);
        }}
      >
        <div className="field" style={{ maxWidth: 200 }}>
          <label htmlFor="news-symbol">Symbol</label>
          <input
            id="news-symbol"
            type="text"
            value={input}
            placeholder="e.g. RR.L or AAPL"
            autoComplete="off"
            spellCheck={false}
            onChange={(event) => setInput(event.target.value)}
          />
        </div>
        <button className="btn btn-primary" type="submit" disabled={busy || input.trim() === ''}>
          {busy ? <Spinner /> : 'Look up'}
        </button>
      </form>

      {error && (
        <div style={{ marginTop: 10 }}>
          <Note tone="warn">{error}</Note>
        </div>
      )}

      {data && symbol && (
        <div style={{ marginTop: 12 }}>
          <p className="prose">
            {data.target.name} ({data.target.exchange}) · search: <span className="mono-small">{data.target.query}</span>
            {data.errors.length > 0 && <span className="down"> · {data.errors.length} source(s) failed</span>}
          </p>
          {assessment ? (
            <div style={{ margin: '10px 0' }}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <span className="mono-small">last verdict {formatRelative(assessment.assessedAt ?? null)}:</span>
                <Badge tone="grey">new long {assessment.openLong ?? '—'}</Badge>
                <Badge tone="grey">new short {assessment.openShort ?? '—'}</Badge>
                <Badge tone="grey">exit long {assessment.exitLong ?? '—'}</Badge>
                <Badge tone="grey">hold through stop {assessment.holdThroughStopLong ? 'yes' : 'no'}</Badge>
                {assessment.materiality && <span className="mono-small">materiality {assessment.materiality}</span>}
              </div>
              {(assessment.summary || assessment.reason) && (
                <p className="reason" style={{ margin: '6px 0 0' }}>
                  {assessment.summary} {assessment.reason}
                </p>
              )}
            </div>
          ) : (
            <p className="prose">Not assessed yet: a verdict is made when a stream proposes a trade in it.</p>
          )}
          {data.items.length === 0 ? (
            <Empty>No news in the window.</Empty>
          ) : (
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {data.items.map((item) => (
                <li key={item.id} style={{ marginTop: 4 }}>
                  <Headline title={item.title} url={item.url} kind={item.kind} />{' '}
                  <span className="mono-small">
                    · {item.kind ?? 'headline'} · {item.source} · {formatRelative(item.publishedAt)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Panel>
  );
}
